import { z } from "zod";
import { ActionBundleReviewSchema, LocalAcceptanceReceiptSchema } from "./atomic-acceptance.js";
import { ClinicDocumentFieldBindingSchema, DocumentSelectionDecisionSchema, GeneratedDocumentArtifactSchema, ORDER_TEST_KEY_MAX_LENGTH, } from "./clinic-documents.js";
import { LabOrderSetRefSchema } from "./form-order-sets.js";
import { LabOrderFastingSchema } from "./orders.js";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
/**
 * Draft-first laboratory ordering.
 *
 * Before the physician signs, the working object is a versioned DRAFT: the
 * AI's proposal lands in it, the physician's edits (ticked tests, manual
 * field values) update it, the preview PDF and the note lines render from
 * it. Nothing governed happens until "Sign & create orders", which runs the
 * agent run, review, selection, generation, AAL2 acceptance and seal in one
 * server call. Each test records who put it there so the audit trail of
 * "AI proposed / physician amended" survives the move.
 */
export const LabOrderDraftTestSourceSchema = z.enum(["ai", "physician"]);
export const LabOrderDraftTestSchema = z
    .object({
    code: z.string().trim().min(1).max(80),
    display: z.string().trim().min(1).max(240),
    codeSystem: z.enum(["LOINC", "local", "free_text"]),
    reason: z.string().trim().min(1).max(1_000),
    specimen: z.string().trim().min(1).max(160),
    fasting: LabOrderFastingSchema,
    source: LabOrderDraftTestSourceSchema,
})
    .strict();
export const LabOrderDraftInputSchema = z
    .object({
    tests: z.array(LabOrderDraftTestSchema).min(1).max(40),
    diagnosisCodes: z.array(z.string().trim().min(1).max(80)).min(1).max(20),
    summary: z.string().trim().min(1).max(500),
    rationale: z.string().trim().min(1).max(2_000),
    /** Reviewed manual field values for the requisition, keyed by field id. */
    manualValues: z.record(z.string().min(1).max(200), z.string().max(10_000)).default({}),
    /** The clinic order set these tests came from. The server keeps it only while it is published. */
    orderSet: LabOrderSetRefSchema.optional(),
})
    .strict()
    .superRefine((draft, context) => {
    const seen = new Set();
    for (const [index, test] of draft.tests.entries()) {
        const key = `${test.codeSystem}\0${test.code}`;
        if (seen.has(key)) {
            context.addIssue({
                code: "custom",
                path: ["tests", index],
                message: "A draft cannot list the same coded test twice",
            });
        }
        seen.add(key);
    }
});
export const LabOrderDraftStatusSchema = z.enum(["draft", "signed", "discarded"]);
export const LabOrderDraftSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema,
    version: z.number().int().positive(),
    status: LabOrderDraftStatusSchema,
    tests: z.array(LabOrderDraftTestSchema).min(1).max(40),
    diagnosisCodes: z.array(z.string().min(1).max(80)).min(1).max(20),
    summary: z.string().min(1).max(500),
    rationale: z.string().min(1).max(2_000),
    manualValues: z.record(z.string().min(1).max(200), z.string().max(10_000)),
    orderSet: LabOrderSetRefSchema.optional(),
    contentHash: Sha256Schema,
    signedActionId: IdSchema.nullable(),
    signedActionVersion: z.number().int().positive().nullable(),
    createdBy: IdSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .strict();
export const UpsertLabOrderDraftRequestSchema = z
    .object({
    /** 0 creates the encounter's draft; otherwise the version being replaced. */
    expectedVersion: z.number().int().nonnegative(),
    draft: LabOrderDraftInputSchema,
    idempotencyKey: IdempotencyKeySchema,
    /**
     * The prepared form set the values were typed on. Entries for fields a clinic instruction
     * fills there are not saved: the instruction's value is what prints.
     */
    preparedSelectionId: IdSchema.optional(),
    /**
     * Set instead when the values were typed on the draft preview of the version being replaced,
     * previewed without these suggested tests. Entries that copy what a clinic instruction fills
     * on those forms are not saved either, so a copy never prints on its own after the test that
     * filled it comes off the order.
     */
    draftPreview: z
        .object({
        declinedConceptIds: z.array(z.string().trim().min(1).max(160)).max(100).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/** A saved entry that is left out, by plain form and field names (never identifiers). */
export const PreparedFormEntrySchema = z
    .object({ formName: z.string().min(1).max(500), fieldLabel: z.string().min(1).max(500) })
    .strict();
/** Non-blocking, plain-language notes about a prepared form set. */
export const PreparedFormNoticesSchema = z.array(z.string().trim().min(1).max(2_000)).max(50);
/**
 * An ordered test that no field of the prepared forms shows. `key` identifies it on the order
 * (a code, or its reviewed name) and is sent back once the physician has added it by hand;
 * `display` is its name on the order.
 */
export const UnplacedOrderTestSchema = z
    .object({
    key: z.string().trim().min(1).max(ORDER_TEST_KEY_MAX_LENGTH),
    display: z.string().trim().min(1).max(500),
})
    .strict();
/**
 * A prepared form set that still misses required information. The forms are returned so the
 * physician can complete them; none of them can be signed until a new preparation is complete.
 * `unplacedTests` lists ordered tests no form field shows: the physician writes each one on the
 * form where it belongs and confirms it. Tests are never named in `message`, which is logged.
 */
export const PreparedFormsIncompleteSchema = z
    .object({
    message: z.string().min(1).max(4_000),
    fields: z.array(PreparedFormEntrySchema).max(200),
    unplacedTests: z.array(UnplacedOrderTestSchema).max(40).optional(),
})
    .strict();
/**
 * An ordered test the physician confirmed they added to the forms by hand. `writtenAs` is what
 * they wrote for it, when the editor could tell ("Syphilis" for an RPR order). It is only ever
 * recorded as a suggestion for a clinic administrator, never applied on its own.
 */
export const PlacedOrderTestSchema = z
    .object({
    key: z.string().trim().min(1).max(ORDER_TEST_KEY_MAX_LENGTH),
    writtenAs: z.array(z.string().trim().min(1).max(240)).max(5).optional(),
})
    .strict();
/** A suggested test as the physician sees it: its plain name, never a code. */
export const FormProposalLabelSchema = z
    .object({
    conceptId: z.string().trim().min(1).max(160),
    label: z.string().trim().min(1).max(500),
})
    .strict();
/**
 * One form of a draft preview as the physician saw it: the exact form version, the pages shown
 * and the hash of the values printed on it. Preparing the forms for signing compares these with
 * the governed forms, so an unchanged set is not downloaded again.
 */
export const PreviewedLabFormSchema = z
    .object({
    documentId: IdSchema,
    documentVersionHash: Sha256Schema,
    includedPages: z.array(z.number().int().positive()).min(1).max(200),
    fieldValuesHash: Sha256Schema,
})
    .strict();
export const PrepareLabOrderDraftRequestSchema = z
    .object({
    expectedDraftVersion: z.number().int().positive(),
    expectedEncounterVersion: z.number().int().positive(),
    idempotencyKey: z.string().min(8).max(128),
    /** Tests suggested by clinic instructions that the physician chose not to order. */
    declinedConceptIds: z.array(z.string().trim().min(1).max(160)).max(100).optional(),
    /** Ordered tests no form field showed, which the physician added to the forms by hand. */
    placedTests: z.array(PlacedOrderTestSchema).max(40).optional(),
    /** The draft preview's forms, in order, when the physician is looking at one. */
    previewedForms: z.array(PreviewedLabFormSchema).max(10).optional(),
})
    .strict();
/**
 * Renders the draft on every form it needs, without preparing it for signing. Nothing is
 * recorded, so the preview is fast; signing still prepares the governed forms.
 */
export const PreviewLabOrderDraftRequestSchema = z
    .object({
    expectedDraftVersion: z.number().int().positive(),
    /** Tests suggested by clinic instructions that the physician chose not to order. */
    declinedConceptIds: z.array(z.string().trim().min(1).max(160)).max(100).optional(),
    /** Keys of ordered tests the physician added to the forms by hand. */
    placedTestKeys: z
        .array(z.string().trim().min(1).max(ORDER_TEST_KEY_MAX_LENGTH))
        .max(40)
        .optional(),
})
    .strict();
/** The published requisition form the draft renders on, with only its editable bindings. */
export const LabOrderDraftDocumentSchema = z
    .object({
    documentId: IdSchema,
    documentVersion: z.number().int().positive(),
    documentVersionHash: Sha256Schema,
    fields: z.array(ClinicDocumentFieldBindingSchema).max(500),
})
    .strict();
/** One form of a draft preview: its editable bindings, plain name, pages shown and value hash. */
export const LabOrderDraftPreviewFormSchema = z
    .object({
    document: LabOrderDraftDocumentSchema,
    name: z.string().min(1).max(500),
    includedPages: z.array(z.number().int().positive()).min(1).max(200),
    fieldValuesHash: Sha256Schema,
})
    .strict();
/**
 * The draft rendered on its whole form set as one PDF, forms in selection order. `notices`,
 * `omittedValues` and `incomplete` mean what they mean on a prepared form set.
 */
export const LabOrderDraftSetPreviewSchema = z
    .object({
    draftVersion: z.number().int().positive(),
    pdfBase64: z.string().min(1),
    forms: z.array(LabOrderDraftPreviewFormSchema).min(1).max(10),
    notices: PreparedFormNoticesSchema.optional(),
    omittedValues: z.array(PreparedFormEntrySchema).max(200).optional(),
    incomplete: PreparedFormsIncompleteSchema.optional(),
})
    .strict();
/**
 * Renders draft CONTENT on every form it needs, the way the saved draft's preview renders it,
 * before that content is saved: the browser previews what it is saving while the save runs.
 * Nothing is recorded but the preview's audit event, and the saved draft is neither read nor
 * written, so the preview never waits for the save. Declines and hand-placed tests mean what they
 * mean on the saved draft's preview.
 */
export const PreviewLabOrderDraftContentRequestSchema = z
    .object({
    draft: LabOrderDraftInputSchema,
    /** Tests suggested by clinic instructions that the physician chose not to order. */
    declinedConceptIds: PreviewLabOrderDraftRequestSchema.shape.declinedConceptIds,
    /** Keys of ordered tests the physician added to the forms by hand. */
    placedTestKeys: PreviewLabOrderDraftRequestSchema.shape.placedTestKeys,
})
    .strict();
/**
 * Draft content rendered on its whole form set: the saved draft's preview without a draft
 * version, since the content previewed has none yet. The caller shows it only for the saved
 * version that holds exactly this content.
 */
export const LabOrderDraftContentPreviewSchema = LabOrderDraftSetPreviewSchema.omit({
    draftVersion: true,
}).strict();
export const LabOrderDraftEnvelopeSchema = z
    .object({
    draft: LabOrderDraftSchema.nullable(),
    document: LabOrderDraftDocumentSchema.nullable(),
})
    .strict();
export const DiscardLabOrderDraftRequestSchema = z
    .object({ expectedVersion: z.number().int().positive() })
    .strict();
export const SignLabOrderDraftRequestSchema = z
    .object({
    expectedDraftVersion: z.number().int().positive(),
    expectedEncounterVersion: z.number().int().positive(),
    /** Exact form identity returned with the PDF the clinician reviewed. */
    previewedDocument: LabOrderDraftDocumentSchema.pick({
        documentId: true,
        documentVersionHash: true,
    }),
    reviewedPreparation: z
        .object({
        runId: IdSchema,
        bundleId: IdSchema,
        selectionId: IdSchema,
        selectionHash: Sha256Schema,
        artifacts: z
            .array(z
            .object({
            id: IdSchema,
            version: z.number().int().positive(),
            contentHash: Sha256Schema,
        })
            .strict())
            .min(1)
            .max(50),
    })
        .strict()
        .optional(),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
/** Everything the signed order produced, so the encounter can render receipts without refetching. */
export const LabOrderSignReceiptSchema = z
    .object({
    runId: IdSchema,
    review: ActionBundleReviewSchema,
    receipt: LocalAcceptanceReceiptSchema,
    selection: DocumentSelectionDecisionSchema,
    artifacts: z.array(GeneratedDocumentArtifactSchema).min(1).max(50),
    draft: LabOrderDraftSchema,
})
    .strict();
export const CancelLabOrderRequestSchema = z
    .object({
    reason: z.string().trim().max(500).optional(),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const LabOrderCancellationReceiptSchema = z
    .object({
    labOrderId: IdSchema,
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    state: z.literal("cancelled"),
    cancelledAt: IsoDateTimeSchema,
    cancelledBy: IdSchema,
    cancelledExpectedEventIds: z.array(IdSchema).max(50),
    replayed: z.boolean(),
})
    .strict();
//# sourceMappingURL=lab-order-drafts.js.map